import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, ArrowRight, Loader2 } from "lucide-react";
import { requestQuote } from "../../lib/api";

const CARGO_TYPES = [
  "Medical & Healthcare",
  "Dental & Orthodontic",
  "Toys & Baby Products",
  "Furnishings & Interiors",
  "Gifts & Souvenirs",
  "Beauty & Personal Care",
  "Textiles & Fashion",
  "Electronics & Electrical",
  "Hardware & Tools",
  "Seasonal & Holiday",
  "Packaging & Stationery",
  "Transport & Recreation",
  "Other",
];

const TIMELINES = [
  "Within 30 days",
  "1–2 months",
  "2–3 months",
  "Flexible / planning ahead",
];

export default function QuoteModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    origin: "Yiwu, China",
    destination: "",
    cargo_type: CARGO_TYPES[0],
    volume: "",
    timeline: TIMELINES[0],
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await requestQuote(form);
      toast.success("Quote request received. We'll come back within one business day.");
      onClose?.();
      setForm({
        name: "",
        email: "",
        company: "",
        origin: "Yiwu, China",
        destination: "",
        cargo_type: CARGO_TYPES[0],
        volume: "",
        timeline: TIMELINES[0],
        notes: "",
      });
    } catch (err) {
      toast.error("Could not submit quote request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      data-testid="quote-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
        data-testid="quote-modal-backdrop"
      />
      <div className="relative w-full max-w-3xl bg-white border border-ink-100 shadow-[0_30px_80px_-20px_rgba(16,24,40,0.45)] max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-ink-100 sticky top-0 bg-white">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal">
              Request a quote
            </p>
            <h2 className="mt-2 font-heading font-black text-ink-900 tracking-tight text-2xl md:text-3xl">
              Tell us your shipment.
            </h2>
            <p className="mt-1 text-ink-500 text-sm">
              We'll come back with a route, price and timeline within one business day.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quote-modal-close"
            className="w-10 h-10 flex items-center justify-center border border-ink-100 hover:border-ink-900 text-ink-900 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="quote-form" className="p-6 md:p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Full name *">
              <input
                type="text"
                required
                data-testid="quote-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="qinput"
                placeholder="Your name"
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                required
                data-testid="quote-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="qinput"
                placeholder="you@company.com"
              />
            </Field>
            <Field label="Company">
              <input
                type="text"
                data-testid="quote-company"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className="qinput"
                placeholder="Optional"
              />
            </Field>
            <Field label="Cargo type">
              <select
                data-testid="quote-cargo"
                value={form.cargo_type}
                onChange={(e) => update("cargo_type", e.target.value)}
                className="qinput bg-white"
              >
                {CARGO_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Origin">
              <input
                type="text"
                data-testid="quote-origin"
                value={form.origin}
                onChange={(e) => update("origin", e.target.value)}
                className="qinput"
                placeholder="Yiwu, China"
              />
            </Field>
            <Field label="Destination">
              <input
                type="text"
                data-testid="quote-destination"
                value={form.destination}
                onChange={(e) => update("destination", e.target.value)}
                className="qinput"
                placeholder="City, Country"
              />
            </Field>
            <Field label="Volume / units">
              <input
                type="text"
                data-testid="quote-volume"
                value={form.volume}
                onChange={(e) => update("volume", e.target.value)}
                className="qinput"
                placeholder="e.g., 5000 units, 1×40' container"
              />
            </Field>
            <Field label="Timeline">
              <select
                data-testid="quote-timeline"
                value={form.timeline}
                onChange={(e) => update("timeline", e.target.value)}
                className="qinput bg-white"
              >
                {TIMELINES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Anything else we should know?">
            <textarea
              rows={3}
              data-testid="quote-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="qinput resize-none"
              placeholder="Special requirements, certifications needed, target landed price…"
            />
          </Field>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-xs text-ink-500">
              We typically reply within 24 hours during business days.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit"
              className="inline-flex items-center justify-center gap-2 bg-signal hover:bg-signal-600 text-white px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Send Quote Request
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>

        <style>{`
          .qinput {
            width: 100%;
            background: #fff;
            border: 1px solid #E4E7EC;
            padding: 11px 13px;
            font-family: 'IBM Plex Sans', sans-serif;
            font-size: 14.5px;
            color: #101828;
            outline: none;
            border-radius: 2px;
            transition: border-color .15s ease, box-shadow .15s ease;
          }
          .qinput::placeholder { color: #98A2B3; }
          .qinput:focus {
            border-color: #FF3B30;
            box-shadow: 0 0 0 3px rgba(255,59,48,0.12);
          }
        `}</style>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
